import React, { useState } from "react";
import CopyButton from "./CopyButton";
import ShareUrlButton from "./ShareUrlButton";
import "./ClipboardDemo.css";

/**
 * Demo component that showcases the clipboard functionality
 */
const ClipboardDemo = () => {
  const [inputText, setInputText] = useState("");
  const [inputUrl, setInputUrl] = useState(
    "https://danials.org/share#data=abc123?shareUrl=https://example.com/shared-link"
  );
  const [paramName, setParamName] = useState("shareUrl");
  const [notifications, setNotifications] = useState([]);

  // Add a notification that automatically removes itself
  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);

    // Auto remove notification after 3 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  };

  // Sample data for different formats
  const sampleData = {
    text: "This is sample text to copy to clipboard.",
    url: "https://example.com",
    json: JSON.stringify(
      { name: "John Doe", email: "john@example.com" },
      null,
      2
    ),
  };

  return (
    <div className="clipboard-demo">
      <h2>Clipboard API Demo</h2>

      <div className="demo-section">
        <h3>Copy Custom Text</h3>
        <div className="input-group">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type something to copy..."
          />
          <CopyButton
            text={inputText}
            disabled={!inputText.trim()}
            onSuccess={() => addNotification("Custom text copied!", "success")}
            onError={(err) => addNotification(err, "error")}
          >
            Copy Text
          </CopyButton>
        </div>
      </div>

      <div className="demo-section">
        <h3>Extract & Copy URL Parameter</h3>
        <p className="instruction">
          This demonstrates extracting and copying just the value of a URL
          parameter (like <code>shareUrl</code>) instead of the entire URL.
        </p>

        <div className="input-group url-input">
          <div className="url-input-row">
            <label>URL:</label>
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="Enter a URL with parameters..."
              className="url-input-field"
            />
          </div>

          <div className="url-input-row">
            <label>Parameter name:</label>
            <input
              type="text"
              value={paramName}
              onChange={(e) => setParamName(e.target.value)}
              placeholder="Parameter to extract (e.g. shareUrl)"
              className="param-input-field"
            />
          </div>

          <ShareUrlButton
            url={inputUrl}
            paramName={paramName}
            buttonText={`Copy ${paramName} Value`}
            onSuccess={() =>
              addNotification(`${paramName} value copied!`, "success")
            }
            onError={(err) => addNotification(err, "error")}
          />
        </div>

        <div className="sample-urls">
          <p>
            <strong>Sample URLs to try:</strong>
          </p>
          <ul>
            <li
              onClick={() =>
                setInputUrl(
                  "https://danials.org/share#data=abc123?shareUrl=https://example.com/shared-link"
                )
              }
            >
              <code>
                https://danials.org/share#data=abc123?shareUrl=https://example.com/shared-link
              </code>
            </li>
            <li
              onClick={() =>
                setInputUrl(
                  "https://danials.org/share?shareUrl=https://example.com/another-link&other=param"
                )
              }
            >
              <code>
                https://danials.org/share?shareUrl=https://example.com/another-link&other=param
              </code>
            </li>
          </ul>
        </div>
      </div>

      <div className="demo-section">
        <h3>Copy Preset Values</h3>
        <div className="preset-buttons">
          <div className="preset-item">
            <div className="preset-value">{sampleData.text}</div>
            <CopyButton
              text={sampleData.text}
              onSuccess={() =>
                addNotification("Sample text copied!", "success")
              }
            >
              Copy Text
            </CopyButton>
          </div>

          <div className="preset-item">
            <div className="preset-value">{sampleData.url}</div>
            <CopyButton
              text={sampleData.url}
              onSuccess={() => addNotification("URL copied!", "success")}
            >
              Copy URL
            </CopyButton>
          </div>

          <div className="preset-item">
            <pre className="preset-value json">{sampleData.json}</pre>
            <CopyButton
              text={sampleData.json}
              onSuccess={() => addNotification("JSON copied!", "success")}
            >
              Copy JSON
            </CopyButton>
          </div>
        </div>
      </div>

      {/* Notification system */}
      <div className="notification-container">
        {notifications.map(({ id, message, type }) => (
          <div key={id} className={`notification ${type}`}>
            {message}
          </div>
        ))}
      </div>

      <div className="info-box">
        <h4>About This Component</h4>
        <p>
          This component demonstrates clipboard operations with graceful
          fallbacks when permissions are denied. It uses a custom React hook
          that tries multiple methods to copy text:
        </p>
        <ol>
          <li>
            Modern Clipboard API (<code>navigator.clipboard</code>)
          </li>
          <li>
            Legacy method (<code>document.execCommand('copy')</code>)
          </li>
          <li>Manual copy via modal dialog when both automated methods fail</li>
        </ol>
        <p>
          The <strong>Extract & Copy URL Parameter</strong> section demonstrates
          extracting and copying just the value of a specified URL parameter
          (like <code>shareUrl</code>) instead of copying the entire URL.
        </p>
      </div>
    </div>
  );
};

export default ClipboardDemo;
